import { chargeSquareCard, SaveSquareCard } from "api/square/square";
import React, { useEffect, useRef, useState } from "react";
import toast from "react-hot-toast";
import {
  CreditCard,
  PaymentForm,
  ApplePay,
  GooglePay,
} from "react-square-web-payments-sdk";
import ButtonPrimary from "shared/Button/ButtonPrimary";

type TokenResult = {
  status: string;
  token?: string;
  errors?: Array<{ message: string }>;
};

type VerifyBuyerResponseDetails = any;

// 1NOW-10711: one id per Pay & Book click, sent with the charge so the backend
// (and Square) can tell a replay of this click from a deliberate new attempt.
const newRequestId = () =>
  typeof crypto !== "undefined" && "randomUUID" in crypto
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(16).slice(2)}`;

interface SquarePaymentFormProps {
  squareDetails: any;
  onPaymentSuccess: (data: any) => void;
  onPaymentError: (error: string) => void;
  amount: number;
  bookingId: string;
  renterId: string;
  rentalAgreementChecked: boolean;
  privacyPolicyCheckedChecked: boolean;
  termsandconditionChecked: boolean;
}

const SquarePaymentForm: React.FC<SquarePaymentFormProps> = ({
  squareDetails,
  onPaymentSuccess,
  onPaymentError,
  amount,
  bookingId,
  renterId,
  rentalAgreementChecked = false,
  privacyPolicyCheckedChecked = false,
  termsandconditionChecked = false,
}) => {
  const [loading, setLoading] = useState(false);
  // 1NOW-10668: this was `let loadingCheck = false` in the component body, so it
  // re-initialised on every render and never actually blocked a second submit.
  // Square's own SDK button is what prevents the double-charge here; this is
  // the guard the code always looked like it had.
  const loadingCheck = useRef(false);
  const handlePaymentFormSubmit = async (tokenResult: TokenResult) => {
    if (loadingCheck.current) return; // Prevent multiple submissions while loading
    // Validate BEFORE claiming the guard. This return is outside the
    // try/finally that releases it, so claiming first would leave the ref
    // stuck true and the form permanently dead.
    if (!tokenResult.token) {
      onPaymentError("Payment token is missing.");
      return;
    }
    loadingCheck.current = true;
    const requestId = newRequestId();
    try {
      setLoading(true);
      const renterIdStr = String(renterId);
      const saveCardResponse = await SaveSquareCard(
        renterIdStr,
        tokenResult.token
      );
      // 1NOW-10669: SaveSquareCard returns {error} on failure, which has no
      // `.card`, so `?.card?.id || ""` sent an empty id and the backend
      // replied "Card ID is required." The renter's card had been refused for
      // a real reason -- bad nonce, Square declined it, customer creation
      // failed -- and that reason was thrown away. Stop here and show what
      // happened.
      if (saveCardResponse?.error || !saveCardResponse?.card?.id) {
        onPaymentError(
          saveCardResponse?.error ??
            "We couldn't save your card. Please check the details and try again."
        );
        return;
      }

      const response = await chargeSquareCard(
        bookingId,
        saveCardResponse.card.id,
        requestId
      );

      if (!response.error) {
        onPaymentSuccess(response);
      } else {
        onPaymentError(response.error);
      }
    } catch (error: unknown) {
      onPaymentError(error instanceof Error ? error.message : String(error));
    } finally {
      loadingCheck.current = false;
      setLoading(false);
    }
  };

  useEffect(() => {
    const style = document.createElement("style");
    style.innerHTML = "#rswp-card-button { display: none !important; }";
    document.head.appendChild(style);
    return () => {
      document.head.removeChild(style);
    };
  }, []);

  const handleCustomPayClick = () => {
    if (termsandconditionChecked !== true) {
      toast.error("Please agree to the Terms and Conditions");
      return;
    }
    if (rentalAgreementChecked !== true) {
      toast.error("Please review and accept the Rental Agreement");
      return;
    }
    if (privacyPolicyCheckedChecked !== true) {
      toast.error("Please agree to Privacy & Cancellation Policies");
      return;
    }
    const squareBtn = document.getElementById("rswp-card-button");
    if (squareBtn) {
      squareBtn.click();
    }
  };

  return (
    <div className="mt-8 pt-8">
      <span className="text-lg text-text-color font-semibold mt-4">
        Payment Information
      </span>
      <div className="mb-6"></div>
      <PaymentForm
        applicationId={squareDetails?.application_id}
        locationId={squareDetails?.location_id}
        cardTokenizeResponseReceived={handlePaymentFormSubmit}
        createPaymentRequest={() => ({
          countryCode: "US",
          currencyCode: "USD",
          total: {
            amount: amount.toString(),
            label: "Total",
          },
        })}
      >
        <CreditCard />
      </PaymentForm>
      <ButtonPrimary
        type="button"
        className="w-full mt-4 rounded-md"
        onClick={handleCustomPayClick}
        disabled={loading}
        aria-label="Pay & Book"
      >
        {loading ? (
          <div className="flex justify-center items-center col-span-full">
            <div className="spinner-border animate-spin inline-block w-6 h-6 border-4 rounded-full border-t-transparent border-secondary-100"></div>
          </div>
        ) : (
          "Pay & Book"
        )}
      </ButtonPrimary>
    </div>
  );
};

export default SquarePaymentForm;
